import { reactive, ref } from "vue";
import { currentService } from "@/views/project-list";
import mime from "mime";

export let fileToUpload = ref();
export let currentDirectory = ref("");
export let uploadGroups: FormData[] = [];
export let serviceFolders: any = {}; // {serviceid: {dirname: {}}}
export let dbServiceFolders: any = {}; // {serviceid: {dirname: {}}}
export let urlCache: { [path: string]: { url: string; expiresAt: number } } = {};

// Persistent AI-index cache for dbfile.vue, keyed by service id → set of storage
// paths CONFIRMED indexed (their `src::<storagePath>` record exists). Module-level
// so it survives dbfile.vue remounts (nav away + back) → no refetch of already-
// indexed files. Only the durable "indexed" state is cached here; "unindexed" is
// re-verified per mount so a file that finished indexing while the page was away
// flips to indexed on return. Reactive so the badges update when entries change.
export const dbIndexedPaths: { [service_id: string]: Set<string> } = reactive({});

type UploadTarget = 'db' | 'host';
type UploadedItemInfo = {
    file: File;
    relPath: string;       // path relative to currentDir (e.g. "sub/x.png" or "x.png")
    storagePath: string;   // full storage key (currentDir + relPath)
    target: UploadTarget;
    storeKey?: string;
    // Bytes of the file this upload OVERWROTE at the same path, or 0 for a new
    // file. Lets the caller apply the true storage delta (file.size -
    // previousSize) instead of over-counting overwrites.
    previousSize?: number;
    // True when the existing file was kept (user chose "Reindex") — no
    // bytes were uploaded, so the caller should reindex the existing file and
    // NOT change storage usage.
    reindexOnly?: boolean;
};
// Per-file decision returned by onBeforeItem when an upload hits an existing
// file at the same path: skip it, keep & reindex, or replace.
type OverwriteDecision = "reindex" | "overwrite" | "skip";
type QueueItem = {
    file: File;
    currentDir: string;
    target: UploadTarget;
    foldersRef?: any;
    storeKey?: string;
    contentTypeMapping?: { [fname: string]: string };
    callback?: (hasOverwritten: boolean) => void;
    onItemUploaded?: (info: UploadedItemInfo) => void | Promise<void>;
    // Optional pre-upload hook (db target). When an existing file is detected
    // at the same path, it is awaited to decide keep-&-reindex vs overwrite.
    onBeforeItem?: (info: UploadedItemInfo) => Promise<OverwriteDecision>;
    // 1-based position of this file within the batch, for the per-file queue
    // display (so concurrent slots show distinct "3/11", "4/11" etc.).
    position?: number;
};

// Per-target upload state. The two UI pages (hosting.vue and dbfile.vue)
// each watch their own target's progress/count so a db-file upload never
// bleeds into the hosting view and vice versa.
type TargetState = {
    queue: QueueItem[];
    activeWorkers: number;
    hasOverwrittenFiles: boolean;
    activeCallbacks: Set<(hasOverwritten: boolean) => void>;
    uploadCount: number[]; // [finished, total]
    uploadProgress: any;   // { name, progress, fileSize, target } — host target only
};

const makeTargetState = (): TargetState => ({
    queue: [],
    activeWorkers: 0,
    hasOverwrittenFiles: false,
    activeCallbacks: new Set(),
    uploadCount: reactive([0, 0]) as unknown as number[],
    uploadProgress: reactive({}),
});

const targetStates: Record<UploadTarget, TargetState> = {
    host: makeTargetState(),
    db: makeTargetState(),
};

// Hosting-page bindings (unchanged export names for backward compatibility).
export const uploadCount = targetStates.host.uploadCount;
export const uploadProgress = targetStates.host.uploadProgress;

// Db-file-page bindings (separate reactive state).
export const dbUploadCount = targetStates.db.uploadCount;
export const dbUploadProgress = targetStates.db.uploadProgress;

// Up to 4 files upload concurrently for the db target; each slot tracks one
// in-flight file. An empty `name` means the slot is idle.
const DB_CONCURRENCY = 4;
export const dbUploadSlots: Array<{ name: string; progress: number; fileSize: number; position: number }> = reactive([
    { name: '', progress: 0, fileSize: 0, position: 0 },
    { name: '', progress: 0, fileSize: 0, position: 0 },
    { name: '', progress: 0, fileSize: 0, position: 0 },
    { name: '', progress: 0, fileSize: 0, position: 0 },
]);

// A single failed db upload: its path plus the original error's code/message so
// the db-file page can group failures by cause (mirrors agent.vue's chatbox
// upload-error report, which records err.code / err.message per attachment).
export interface DbUploadFailure {
    name: string;
    code: string;
    message: string;
}
// Files that failed during the current db upload batch. A failed file no longer
// aborts the batch — it's recorded here (with its cause), counted as processed
// so the progress bar still completes, and reported all at once when the batch
// settles.
const dbUploadFailed: DbUploadFailure[] = [];
// Snapshot of the most recently settled db batch's failures. `token` bumps on
// every db-batch completion so the db-file page can react and surface a summary
// modal listing the failed files and why each one failed.
export const dbUploadResult = reactive<{ token: number; failed: DbUploadFailure[] }>({ token: 0, failed: [] });

function reportDbBatchDone() {
    dbUploadResult.failed = [...dbUploadFailed];
    dbUploadResult.token++;
    dbUploadFailed.length = 0;
}

async function processUploadQueue(target: UploadTarget) {
    const state = targetStates[target];
    const maxWorkers = target === 'db' ? DB_CONCURRENCY : 1;
    if (state.activeWorkers >= maxWorkers) return;
    state.activeWorkers++;

    // Each db worker owns one progress slot for its entire lifetime.
    // Host target uses the single state.uploadProgress object (slotIdx === -1).
    const slotIdx = target === 'db' ? dbUploadSlots.findIndex(s => !s.name) : -1;

    try {
        while (state.queue.length > 0) {
            let item = state.queue.shift()!;
            let f = item.file;
            let currentDir = item.currentDir;

            const progressName = currentDir + (f.webkitRelativePath || f.name);
            if (slotIdx >= 0) {
                dbUploadSlots[slotIdx].name = progressName;
                dbUploadSlots[slotIdx].progress = 0;
                dbUploadSlots[slotIdx].fileSize = f.size;
                dbUploadSlots[slotIdx].position = item.position || 0;
            } else {
                state.uploadProgress.name = progressName;
                state.uploadProgress.progress = 0;
                state.uploadProgress.fileSize = f.size;
                state.uploadProgress.target = target;
            }

            const relPath = f.webkitRelativePath || f.name;
            const storagePath = currentDir + relPath;

            // One upload attempt. Fresh FormData per attempt so a retry after an
            // EXISTS probe starts clean. check_existence makes the server reject
            // an already-present file (EXISTS) BEFORE uploading the bytes.
            const doUpload = (checkExistence: boolean) => {
                const fd = new FormData();
                fd.append("files[]", f);
                return currentService.value.uploadHostFiles(fd, {
                    contentTypeMapping: item.contentTypeMapping,
                    prefix: currentDir,
                    target,
                    ...(checkExistence ? { check_existence: true } : {}),
                    progress: (e) => {
                        if (e.status !== "progress") return;
                        const percent = Math.floor(e.progress);
                        if (slotIdx >= 0) {
                            dbUploadSlots[slotIdx].progress = percent;
                        } else {
                            state.uploadProgress.progress = percent;
                        }
                    },
                });
            };

            // True when the user chose "Reindex" for an existing file:
            // nothing uploaded, so skip the folder bookkeeping and storage delta.
            let reindexOnly = false;
            // True when the user chose "skip" for an existing file: leave it
            // untouched entirely — no upload, no reindex, no bookkeeping.
            let skipped = false;

            try {
                if (target === 'db' && item.onBeforeItem) {
                    // Probe with check_existence; on EXISTS ask how to resolve.
                    try {
                        await doUpload(true);
                    } catch (probeErr: any) {
                        const code = probeErr?.code ?? probeErr?.body?.code;
                        if (code !== 'EXISTS') throw probeErr;
                        const choice = await item.onBeforeItem({ file: f, relPath, storagePath, target, storeKey: item.storeKey });
                        if (choice === 'overwrite') {
                            await doUpload(false); // replace the existing file
                        } else if (choice === 'skip') {
                            skipped = true;        // leave it untouched
                        } else {
                            reindexOnly = true;    // keep it; Reindex
                        }
                    }
                } else {
                    await doUpload(false);
                }
            } catch (err: any) {
                if (target === 'db') {
                    // Don't abort the batch. Record the failure and its cause,
                    // count it as processed (so the progress bar still reaches
                    // 100% and the slot frees for the next file), then continue
                    // with the rest of the queue. All failures are surfaced
                    // together once the batch settles (see reportDbBatchDone /
                    // dbUploadResult). Code/message are extracted the same way
                    // agent.vue's recordAttachmentError does, so the shared
                    // groupAttachmentFailures helper can group them by cause.
                    dbUploadFailed.push({
                        name: progressName,
                        code: (err && (err.code || err?.body?.code)) || "",
                        message:
                            (err && (err.message || err?.body?.message)) ||
                            (typeof err === "string" ? err : ""),
                    });
                    state.uploadCount[0]++;
                    continue;
                }
                // Host target: abort the whole batch on the first error.
                // First worker to hit an error fires callbacks; others see empty activeCallbacks and exit.
                if (state.activeCallbacks.size > 0) {
                    state.uploadCount[0] = 0;
                    state.uploadCount[1] = 0;
                    state.uploadProgress.name = "";
                    state.uploadProgress.progress = 0;
                    state.uploadProgress.fileSize = 0;
                    state.uploadProgress.target = "";
                    state.queue.length = 0;
                    state.hasOverwrittenFiles = false;
                    let callbacks = [...state.activeCallbacks];
                    state.activeCallbacks.clear();
                    for (let cb of callbacks) {
                        cb(false);
                    }
                }
                return;
            }

            // "Skip" — existing file left untouched. Nothing uploaded, no
            // reindex, no per-item hook, no folder bookkeeping or storage delta.
            // Count it processed (so the progress bar completes and the slot
            // frees), then continue with the rest of the queue.
            if (skipped) {
                state.uploadCount[0]++;
                continue;
            }

            // "Reindex" — existing file kept, nothing uploaded. Fire the
            // per-item hook so the caller can reindex it, then skip folder
            // bookkeeping and the storage delta entirely.
            if (reindexOnly) {
                if (item.onItemUploaded) {
                    try {
                        void item.onItemUploaded({ file: f, relPath, storagePath, target, storeKey: item.storeKey, reindexOnly: true });
                    } catch (cbErr) {
                        console.error("onItemUploaded callback failed", cbErr);
                    }
                }
                state.uploadCount[0]++;
                continue;
            }

            // Upload succeeded — do bookkeeping in the while loop (awaited) so
            // folder insertions for every file are guaranteed complete before
            // "all done" fires. Previously this ran inside the progress callback
            // which was not awaited, causing a race where getFileList() could be
            // called before concurrent insertions finished.

            let fPathArr = (f.webkitRelativePath || f.name).split("/");
            let flName = fPathArr.pop();
            let fPath = currentDir + fPathArr.join("/");
            if (fPath.slice(-1) === "/") {
                fPath = fPath.slice(0, -1);
            }

            // Size of the file this upload overwrites at the same path (0 if
            // new). Captured in the folder bookkeeping below — where the pager
            // still holds the old item before editItem replaces it — so the
            // per-item hook (fired after bookkeeping) can report the true
            // storage delta instead of the full new size.
            let previousFileSize = 0;

            let _storeKey = item.storeKey || currentService.value.service.subdomain;
            let fileObj: any = {
                name: flName,
                size: f.size,
                path: _storeKey + (fPath === "" ? "" : "/" + fPath),
                upl: new Date().getTime(),
            };

            let _foldersRef = item.foldersRef || serviceFolders;
            let fPathSplit = fPath.split("/");

            if (fPathSplit.length === 1) {
                if (fPathSplit[0] === "") {
                    let folder = _foldersRef[_storeKey]["!"].pager;
                    if (folder.list?.[flName]) {
                        previousFileSize = folder.list[flName].size || 0;
                        state.hasOverwrittenFiles = true;
                        await folder.editItem(fileObj);
                    } else {
                        await folder.insertItems(fileObj);
                    }
                } else {
                    let folder = _foldersRef[_storeKey]["!"].pager;
                    if (!folder.list?.["#" + fPathSplit[0]]) {
                        let obj = Object.assign({}, fileObj, {
                            name: "#" + fPathSplit[0],
                            cnt: 0,
                            size: 0,
                            path: _storeKey,
                        });
                        await folder.insertItems(obj);
                    } else {
                        let obj = folder.list?.["#" + fPathSplit[0]];
                        obj.size = (obj.size || 0) + f.size;
                        obj.cnt = (obj.cnt || 0) + 1;
                        await folder.editItem(obj);
                    }

                    if (_foldersRef[_storeKey][fPathSplit[0]]) {
                        let folder = _foldersRef[_storeKey][fPathSplit[0]].pager;
                        if (folder.list?.[flName]) {
                            previousFileSize = folder.list[flName].size || 0;
                            state.hasOverwrittenFiles = true;
                            await folder.editItem(fileObj);
                        } else {
                            await folder.insertItems(fileObj);
                        }
                    }
                }
            } else {
                // has nested folders
                let pathIdx = fPathSplit.length;
                let fp = fPathSplit.slice(0, pathIdx).join("/");
                if (_foldersRef[_storeKey][fp]) {
                    let folder = _foldersRef[_storeKey][fp].pager;
                    if (folder.list?.[flName]) {
                        previousFileSize = folder.list[flName].size || 0;
                        state.hasOverwrittenFiles = true;
                        await folder.editItem(fileObj);
                    } else {
                        await folder.insertItems(fileObj);
                    }
                }
                do {
                    if (pathIdx) {
                        fileObj = Object.assign({}, fileObj);
                        let fp = fPathSplit.slice(0, pathIdx);
                        let fldName = "#" + fp.pop();
                        let fpStr = fp.join("/") || "!";
                        fileObj.name = fldName;
                        if (_foldersRef[_storeKey][fpStr]) {
                            let folder = _foldersRef[_storeKey][fpStr].pager;
                            if (folder.list?.[fldName]) {
                                let obj = folder.list?.[fldName];
                                obj.size = (obj.size || 0) + f.size;
                                obj.cnt = (obj.cnt || 0) + 1;
                                obj.path = _storeKey + (fpStr.replace("!", "") ? "/" + fpStr : "");
                                await folder.editItem(obj);
                            } else {
                                fileObj.cnt = 1;
                                fileObj.size = f.size;
                                fileObj.path = _storeKey + (fpStr.replace("!", "") ? "/" + fpStr : "");
                                await folder.insertItems(fileObj);
                            }
                        }
                    }
                } while (pathIdx--);
            }

            // Per-item hook: fired after this file's folder bookkeeping (so
            // previousSize is known) but still per-file, not awaited — the
            // caller acts on each finished file without waiting for the batch.
            if (item.onItemUploaded) {
                try {
                    void item.onItemUploaded({
                        file: f,
                        relPath,
                        storagePath,
                        target,
                        storeKey: item.storeKey,
                        previousSize: previousFileSize,
                        reindexOnly: false,
                    });
                } catch (cbErr) {
                    console.error("onItemUploaded callback failed", cbErr);
                }
            }

            state.uploadCount[0]++;
        }
    } finally {
        state.activeWorkers--;
        if (slotIdx >= 0) {
            dbUploadSlots[slotIdx].name = '';
            dbUploadSlots[slotIdx].progress = 0;
            dbUploadSlots[slotIdx].fileSize = 0;
            dbUploadSlots[slotIdx].position = 0;
        }

        // Fire the completion callback only when the last active worker exits.
        // At this point every worker's while loop has finished, so all folder
        // insertions are guaranteed complete (no race with concurrent callbacks).
        if (state.activeWorkers === 0 && state.uploadCount[0] > 0 && state.uploadCount[0] === state.uploadCount[1]) {
            if (!state.activeCallbacks.size) {
                if (target === 'db') reportDbBatchDone();
                return;
            }
            let callbacks = [...state.activeCallbacks];
            let overwritten = state.hasOverwrittenFiles;
            state.uploadCount[0] = 0;
            state.uploadCount[1] = 0;
            if (slotIdx < 0) {
                state.uploadProgress.name = "";
                state.uploadProgress.progress = 0;
                state.uploadProgress.fileSize = 0;
                state.uploadProgress.target = "";
            }
            state.hasOverwrittenFiles = false;
            state.activeCallbacks.clear();
            if (target === 'db') reportDbBatchDone();
            for (let cb of callbacks) {
                cb(overwritten);
            }
        }
    }
}

export let uploadFiles = async (
    files: File[],
    callback?: (hasOverwritten: boolean) => void,
    contentTypeMapping?: { [fname: string]: string },
    options?: {
        target?: UploadTarget;
        currentDir?: string;
        foldersRef?: any;
        storeKey?: string;
        onItemUploaded?: (info: UploadedItemInfo) => void | Promise<void>;
        onBeforeItem?: (info: UploadedItemInfo) => Promise<OverwriteDecision>;
    }
) => {
    const target: UploadTarget = options?.target === 'db' ? 'db' : 'host';

    if (target !== 'db') {
        let sd = currentService.value.service.subdomain;
        if (!sd) {
            throw new Error("No subdomain found");
        }
        if ((sd && sd[0] === "*") || sd[0] === "+") {
            throw new Error("Cannot upload files to pending subdomain");
        }
    }

    const state = targetStates[target];

    if (target === 'db' && state.activeWorkers === 0) {
        // Fresh db batch (nothing in flight): drop leftover failure records
        // from the previous batch so this batch reports only its own failures.
        dbUploadFailed.length = 0;
    }

    // 1-based position within the batch. Continues from the current total so a
    // second upload merged into a running batch keeps numbering sequential.
    let position = state.uploadCount[1];
    state.uploadCount[1] += files.length;
    let currentDir = options?.currentDir !== undefined
        ? (options.currentDir ? options.currentDir + "/" : "")
        : (currentDirectory.value ? currentDirectory.value + "/" : "");

    if (callback) {
        state.activeCallbacks.add(callback);
    }

    for (let f of files) {
        position++;
        state.queue.push({ file: f, currentDir, target, foldersRef: options?.foldersRef, storeKey: options?.storeKey, contentTypeMapping, callback, onItemUploaded: options?.onItemUploaded, onBeforeItem: options?.onBeforeItem, position });
    }

    // For the db target, spawn up to DB_CONCURRENCY workers in parallel.
    // The activeWorkers guard inside processUploadQueue prevents excess spawns.
    const workerCount = target === 'db' ? DB_CONCURRENCY : 1;
    for (let i = 0; i < workerCount; i++) {
        processUploadQueue(target);
    }
};

let contentTypeMapping: { [filename: string]: string } = {};

function traverseFileTree(item: { [key: string]: any }, path = "") {
    return new Promise((resolve) => {
        if (item.isFile) {
            item.file(function (file: { [key: string]: any }) {
                if (!file.type) {
                    function getExtensionOfFilename(filename: string) {
                        var _fileLen = filename.length;
                        var _lastDot = filename.lastIndexOf(".");
                        var _fileExt = filename
                            .substring(_lastDot, _fileLen)
                            .toLowerCase();
                        return _fileExt;
                    }
                    let type = getExtensionOfFilename(file.name);
                    contentTypeMapping[file.name] = mime.getType(type);
                }
                resolve([{ file, path: path + file.name }]);
            });
        } else if (item.isDirectory) {
            let dirReader = item.createReader();
            dirReader.readEntries(async function (
                entries: { [key: string]: any }[]
            ) {
                let promises = entries.map((entry) =>
                    traverseFileTree(entry, path + item.name + "/")
                );
                let results = await Promise.all(promises);
                resolve([].concat(...results));
            });
        }
    });
}

export let onDrop = async (
    e: any,
    callback?: (hasOverwritten: boolean) => void,
    options?: {
        target?: 'db' | 'host';
        currentDir?: string;
        foldersRef?: any;
        storeKey?: string;
        onItemUploaded?: (info: UploadedItemInfo) => void | Promise<void>;
        onBeforeItem?: (info: UploadedItemInfo) => Promise<OverwriteDecision>;
    }
) => {
    e.preventDefault();

    let items = e.dataTransfer.items;
    let formData = new FormData();
    let allFiles = [];
    let filePromises = [];

    for (let i = 0; i < items.length; i++) {
        let item = items[i].webkitGetAsEntry();

        if (item) {
            filePromises.push(traverseFileTree(item));
        }
    }

    let allFileGroups = await Promise.all(filePromises);
    allFiles = [].concat(...allFileGroups);

    allFiles.forEach(({ file, path }) => {
        formData.append("files[]", file, path);
    });

    uploadFiles(
        formData.getAll("files[]") as File[],
        callback,
        contentTypeMapping,
        options
    );
};
